import type { Restaurant } from "api-types";
import {
  nonPrimaryLocationFromFirestoreDocument,
  primaryLocationfromRestaurant,
} from "dashboard/src/models/locations";
import {
  Timestamp,
  getDocs,
  orderBy,
  query,
  setDoc,
} from "firebase/firestore/lite";

import { FirestoreRefs } from "igfd-commons/src/firebase/refs";

import type {
  FirestoreLocationDocument,
  NonPrimaryLocation,
  RestaurantLocation,
} from "@dashboard/types";

export interface LocationApi {
  // Returns all locations for the given restaurant.
  // The primary location is created based on the restaurant provided.
  fetchLocations(restaurant: Restaurant): Promise<RestaurantLocation[]>;

  // Updates an existing location.
  updateLocation(nonPrimaryLocation: NonPrimaryLocation): Promise<void>;

  // Creates a new location.
  newLocation(location: NonPrimaryLocation): Promise<void>;

  deleteLocation(restaurantUuid: string, locationId: string): Promise<void>;
}

/**
 * An implementation of the LocationApi that uses Firestore as the backend.
 */
export class FirestoreBackedLocationApi implements LocationApi {
  private readonly firestoreRefs: FirestoreRefs;

  constructor(firestoreRefs: FirestoreRefs) {
    this.firestoreRefs = firestoreRefs;
  }

  public fetchLocations(restaurant: Restaurant): Promise<RestaurantLocation[]> {
    const nonPrimaryLocationsPromise = getDocs(
      query(
        this.firestoreRefs.locations(restaurant.uuid),
        orderBy("createdAt", "asc")
      )
    ).then((querySnapshot) =>
      querySnapshot.docs
        .map((doc) => doc.data() as FirestoreLocationDocument)
        // we will only show locations that are operational
        // the ability to close a business is not yet implemented.
        .filter((location) => location.businessStatus === "operational")
        .map((doc) => nonPrimaryLocationFromFirestoreDocument(doc))
    );

    return nonPrimaryLocationsPromise.then((nonPrimaryLocations) => {
      const primaryLocation = primaryLocationfromRestaurant(
        restaurant,
        nonPrimaryLocations
      );
      return [primaryLocation, ...nonPrimaryLocations];
    });
  }

  updateLocation(nonPrimaryLocation: NonPrimaryLocation): Promise<void> {
    const { isPrimary, ...updates } = nonPrimaryLocation;
    return setDoc(
      this.firestoreRefs.location(
        nonPrimaryLocation.restaurantUuid,
        nonPrimaryLocation.id
      ),
      updates,
      {
        merge: true,
      }
    );
  }

  newLocation(location: NonPrimaryLocation): Promise<void> {
    const { isPrimary, ...locationWithoutIsPrimary } = location;
    const docData = {
      ...locationWithoutIsPrimary,

      appStatus: "unlisted", // newly created locations are always unlisted
      businessStatus: "operational", // newly created locations are always operational

      createdAt: Timestamp.now(),
      restaurantRef: this.firestoreRefs.restaurant(location.restaurantUuid),
    } as FirestoreLocationDocument;

    return setDoc(
      this.firestoreRefs.location(location.restaurantUuid, location.id),
      docData,
      { merge: true }
    );
  }

  // Currently implemented as setting the business status to closed_permanently.
  public deleteLocation(
    restaurantUuid: string,
    locationId: string
  ): Promise<void> {
    return setDoc(
      this.firestoreRefs.location(restaurantUuid, locationId),
      {
        businessStatus: "closed_permanently",
      },
      { merge: true }
    );
  }
}
